// Booking detail page with participant actions: cancel, mark no-show, mark complete.
// Wired to the safeguards server functions for tiered refunds + dispute escalation.
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { cancelBooking, reportNoShow } from "@/lib/safeguards.functions";
import { customerConfirmCompletion, markBookingCompleted, markBookingStarted } from "@/lib/bookings.functions";
import { BookingTimeline } from "@/components/site/BookingTimeline";
import { ReviewForm } from "@/components/site/ReviewForm";
import { ReviewThread } from "@/components/site/ReviewThread";
import { BookingChat } from "@/components/site/BookingChat";
import { HelperLiveShare } from "@/components/site/HelperLiveShare";
import { CustomerLiveTracker } from "@/components/site/CustomerLiveTracker";
import { BookingPhotos } from "@/components/site/BookingPhotos";
import { track } from "@/lib/funnel-track";
import { AlertCircle, ArrowLeft, CalendarClock, CheckCircle2, Loader2, MapPin, MessageSquare, ShieldAlert, XCircle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/bookings/$id")({
  head: () => ({ meta: [{ title: "Booking — Peopels" }, { name: "robots", content: "noindex" }] }),
  component: BookingDetail,
});

type Booking = {
  id: string; status: string; payment_status: string;
  total_amount: number | null; helper_payout: number | null; refund_amount: number | null; cancellation_fee: number | null;
  scheduled_start: string | null; actual_start: string | null; actual_end: string | null;
  cancellation_reason: string | null; cancelled_at: string | null;
  customer_confirmed_completion_at: string | null; payout_eligible_at: string | null;
  payout_status: string | null;
  customer_id: string; helper_id: string;
  job: { id: string; title: string; postcode: string | null; description: string | null } | null;
};

function BookingDetail() {
  const { id } = useParams({ from: "/bookings/$id" });
  const navigate = useNavigate();
  const cancel = useServerFn(cancelBooking);
  const noShow = useServerFn(reportNoShow);
  const complete = useServerFn(markBookingCompleted);
  const customerConfirm = useServerFn(customerConfirmCompletion);
  const startFn = useServerFn(markBookingStarted);

  const [booking, setBooking] = useState<Booking | null>(null);
  const [me, setMe] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hasReview, setHasReview] = useState(false);

  const loadBooking = async (uid: string) => {
    const { data, error } = await supabase
      .from("bookings")
      .select("id, status, payment_status, total_amount, helper_payout, refund_amount, cancellation_fee, scheduled_start, actual_start, actual_end, cancellation_reason, cancelled_at, customer_confirmed_completion_at, payout_eligible_at, payout_status, customer_id, helper_id, job:jobs(id, title, postcode, description)")
      .eq("id", id).single();
    if (error || !data) { toast.error("Booking not found"); navigate({ to: "/dashboard" }); return; }
    setBooking(data as unknown as Booking);
    const { data: r } = await supabase.from("reviews").select("id").eq("booking_id", id).eq("reviewer_id", uid).maybeSingle();
    setHasReview(!!r);
  };

  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) { navigate({ to: "/auth", search: { redirect: `/bookings/${id}` } }); return; }
      setMe(u.user.id);
      await loadBooking(u.user.id);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  if (loading || !booking) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const isCustomer = me === booking.customer_id;
  const isHelper = me === booking.helper_id;
  const isActive = ["pending", "in_progress"].includes(booking.status);
  const canCancel = isActive && booking.status !== "completed";
  const canNoShow = isActive && booking.scheduled_start && new Date(booking.scheduled_start).getTime() < Date.now() - 15 * 60_000;
  const canStart = isHelper && booking.status === "pending" && booking.payment_status === "paid";
  const canComplete = isHelper && booking.status === "in_progress";
  const canCustomerConfirm = isCustomer && booking.status === "completed" && !booking.customer_confirmed_completion_at && booking.payout_status !== "paid";

  async function onCancel() {
    const reason = window.prompt("Why are you cancelling? (this is shared with the other party)");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      const r = await cancel({ data: { bookingId: booking!.id, reason: reason.trim() } });
      toast.success(`Cancelled. ${r.refundAmount > 0 ? `£${r.refundAmount.toFixed(2)} refunded.` : "No refund per policy."}`);
      window.location.reload();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Cancel failed"); }
    finally { setBusy(false); }
  }
  async function onNoShow() {
    const notes = window.prompt("Describe what happened (min 3 chars). Our team will review within 24 hours.");
    if (!notes || notes.trim().length < 3) return;
    setBusy(true);
    try {
      await noShow({ data: { bookingId: booking!.id, notes: notes.trim() } });
      toast.success("No-show reported — opening dispute…");
      navigate({ to: "/bookings/$id/dispute", params: { id: booking!.id } });
    } catch (err) { toast.error(err instanceof Error ? err.message : "Report failed"); }
    finally { setBusy(false); }
  }
  async function onStart() {
    setBusy(true);
    try {
      await startFn({ data: { bookingId: booking!.id } });
      await track("booking_started", { booking_id: booking!.id });
      toast.success("Marked as started"); window.location.reload();
    }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
    finally { setBusy(false); }
  }
  async function onComplete() {
    if (!window.confirm("Mark this job complete? The customer then has 48 hours to confirm before payout auto-releases.")) return;
    setBusy(true);
    try {
      await complete({ data: { bookingId: booking!.id } });
      await track("booking_completed", { booking_id: booking!.id });
      toast.success("Marked complete. The customer can confirm to release payout instantly."); window.location.reload();
    }
    catch (err) { toast.error(err instanceof Error ? err.message : "Failed"); }
    finally { setBusy(false); }
  }
  async function onCustomerConfirm() {
    if (!window.confirm("Confirm the job was completed satisfactorily? This releases the payout to your Helper immediately.")) return;
    setBusy(true);
    try {
      await customerConfirm({ data: { bookingId: booking!.id } });
      await track("booking_completed", { booking_id: booking!.id, customer_confirmed: true });
      toast.success("Thanks — payout released to your Helper."); window.location.reload();
    }
    catch (err) { toast.error(err instanceof Error ? err.message : "Confirm failed"); }
    finally { setBusy(false); }
  }

  const statusTone =
    booking.status === "completed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
    booking.status === "cancelled" || booking.status === "refunded" ? "bg-muted text-muted-foreground border-border" :
    booking.status === "disputed" ? "bg-amber-50 text-amber-800 border-amber-200" :
    "bg-primary/10 text-primary border-primary/20";

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <Header />
      <main id="main-content" className="mx-auto w-full max-w-3xl flex-1 px-4 py-8 sm:px-6">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to dashboard</Link>

        <header className="mt-4 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold sm:text-3xl">{booking.job?.title ?? "Booking"}</h1>
            <p className="mt-1 inline-flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              {booking.job?.postcode && (<span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{booking.job.postcode}</span>)}
              {booking.scheduled_start && (<span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />{new Date(booking.scheduled_start).toLocaleString("en-GB")}</span>)}
            </p>
          </div>
          <span className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusTone}`}>{booking.status}</span>
        </header>

        <section className="mt-6 grid gap-4 sm:grid-cols-3">
          <Stat label="Total" value={`£${Number(booking.total_amount ?? 0).toFixed(2)}`} />
          <Stat label={isHelper ? "Your payout" : "Payment"} value={isHelper ? `£${Number(booking.helper_payout ?? 0).toFixed(2)}` : booking.payment_status} />
          {booking.refund_amount && booking.refund_amount > 0 ? (
            <Stat label="Refunded" value={`£${Number(booking.refund_amount).toFixed(2)}`} tone="warn" />
          ) : (
            <Stat label="Scheduled" value={booking.scheduled_start ? new Date(booking.scheduled_start).toLocaleDateString("en-GB") : "—"} />
          )}
        </section>

        {/* Lifecycle timeline — always visible so both parties know what's next */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="font-display text-base font-semibold sm:text-lg">What happens next</h2>
          <p className="mt-1 text-xs text-muted-foreground">Live status of your booking — refreshes when either party takes action.</p>
          <div className="mt-4">
            <BookingTimeline
              status={booking.status}
              paymentStatus={booking.payment_status}
              isHelper={isHelper}
              hasReview={hasReview}
              customerConfirmedAt={booking.customer_confirmed_completion_at}
              payoutEligibleAt={booking.payout_eligible_at}
              payoutStatus={booking.payout_status}
            />
          </div>
        </section>

        {/* Real-time in-app chat per booking */}
        {me && (isCustomer || isHelper) && !["cancelled", "refunded"].includes(booking.status) && (
          <BookingChat bookingId={booking.id} currentUserId={me} otherPartyName={isCustomer ? "your Helper" : "your customer"} />
        )}

        {/* Live ETA + location share */}
        {isHelper && ["pending", "accepted", "in_progress"].includes(booking.status) && (
          <HelperLiveShare bookingId={booking.id} />
        )}
        {isCustomer && ["pending", "accepted", "in_progress"].includes(booking.status) && (
          <CustomerLiveTracker bookingId={booking.id} />
        )}

        {/* Pre/post-job photo evidence (visible to both parties) */}
        {me && (isCustomer || isHelper) && (
          <BookingPhotos
            bookingId={booking.id}
            isHelper={isHelper}
            helperUserId={isHelper ? me : null}
            bookingStatus={booking.status}
          />
        )}




        {/* Inline review prompt — fired immediately after completion */}
        {booking.status === "completed" && (isCustomer || isHelper) && !hasReview && (
          <section className="mt-6" onMouseEnter={() => void track("review_prompt_view", { booking_id: booking.id })}>
            <ReviewForm bookingId={booking.id} audience={isCustomer ? "customer" : "helper"} onSubmitted={() => setHasReview(true)} />
          </section>
        )}

        {/* Two-way review thread — visible to participants and (when revealed) the public */}
        {booking.status === "completed" && (isCustomer || isHelper) && me && (
          <ReviewThread bookingId={booking.id} currentUserId={me} />
        )}

        {booking.status === "cancelled" && (
          <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-4 text-sm">
            <p className="font-medium">Cancelled{booking.cancelled_at ? ` on ${new Date(booking.cancelled_at).toLocaleString("en-GB")}` : ""}</p>
            {booking.cancellation_reason && <p className="mt-1 text-muted-foreground">Reason: {booking.cancellation_reason}</p>}
          </div>
        )}

        {/* Actions */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 shadow-card">
          <h2 className="font-display text-lg font-semibold">Actions</h2>
          <p className="mt-1 text-xs text-muted-foreground">Cancellations follow our <a href="/cancellation" className="underline">Cancellation Policy</a>: platform and service fees are non-refundable, and cancelling an accepted booking without a valid reason may retain up to 50% of the booking value to cover the Professional&apos;s reserved time, travel and admin costs. Helper-initiated cancellations always full-refund the customer. Peopels reviews valid-reason cancellations (duplicate booking, professional unavailable, safety, emergencies) for a full or partial refund.</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {canStart && (
              <Button onClick={onStart} disabled={busy}><CheckCircle2 className="mr-2 h-4 w-4" />Mark started</Button>
            )}
            {canComplete && (
              <Button onClick={onComplete} disabled={busy}><CheckCircle2 className="mr-2 h-4 w-4" />Mark complete</Button>
            )}
            {canCustomerConfirm && (
              <Button onClick={onCustomerConfirm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700"><CheckCircle2 className="mr-2 h-4 w-4" />Confirm completion · release payout</Button>
            )}
            {canCancel && (
              <Button variant="outline" onClick={onCancel} disabled={busy}><XCircle className="mr-2 h-4 w-4" />Cancel booking</Button>
            )}
            {canNoShow && (
              <Button variant="outline" onClick={onNoShow} disabled={busy}><ShieldAlert className="mr-2 h-4 w-4" />Report no-show</Button>
            )}
            <Button asChild variant="ghost"><Link to="/bookings/$id/dispute" params={{ id: booking.id }}><AlertCircle className="mr-2 h-4 w-4" />Open dispute</Link></Button>
            <Button asChild variant="ghost"><Link to="/dashboard"><MessageSquare className="mr-2 h-4 w-4" />Messages</Link></Button>
          </div>

          {!isCustomer && !isHelper && (
            <p className="mt-4 text-sm text-muted-foreground">You don't have access to actions on this booking.</p>
          )}
        </section>

        {booking.job?.description && (
          <section className="mt-6 rounded-2xl border border-border bg-card p-5">
            <h3 className="font-display text-base font-semibold">Job details</h3>
            <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{booking.job.description}</p>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className={`rounded-xl border border-border p-3 ${tone === "warn" ? "bg-amber-50" : "bg-card"}`}>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-lg font-semibold capitalize">{value}</p>
    </div>
  );
}
